import { useId } from 'react'

import { ELEMENT_ORDER, ELEMENT_RU, elementColor } from '../../colors'
import type { ElementId } from '../../colors'

/**
 * Поток стихий: пять узлов по кругу в порядке порождения (дерево → огонь →
 * земля → металл → вода → дерево), стрелки питания по кольцу. Размер узла —
 * доля стихии в карте, если она известна; пустая стихия рисуется пунктиром,
 * перегруженная — двойным кольцом, стихия текущего периода — золотым кольцом.
 *
 * Проценты приходят из /api/systems (натальная карта). Если их нет, схема всё
 * равно осмысленна: она показывает, куда встаёт стихия периода.
 */
export interface ElementShare {
  element: string
  titleRu?: string
  percent?: number
}

const FIELD = 260
const CENTER_X = FIELD / 2
const CENTER_Y = 124
const ORBIT = 82
const BASE_RADIUS = 15

function nodePosition(index: number): { x: number; y: number } {
  const angle = ((index * 72 - 90) * Math.PI) / 180
  return { x: CENTER_X + ORBIT * Math.cos(angle), y: CENTER_Y + ORBIT * Math.sin(angle) }
}

export function ElementFlow({
  elements = [],
  highlight,
  missing = [],
  overloaded = [],
}: {
  elements?: ElementShare[]
  highlight?: string | null
  missing?: string[]
  overloaded?: string[]
}) {
  const arrowId = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const shares = new Map(elements.map((item) => [item.element, item.percent ?? null]))
  const hasPercents = elements.some((item) => typeof item.percent === 'number')

  const radiusOf = (element: ElementId): number => {
    const percent = shares.get(element)
    if (!hasPercents || percent === null || percent === undefined) return BASE_RADIUS
    // 20% — ровная доля; узел ходит примерно от 11 до 26.
    return Math.max(11, Math.min(26, 11 + (percent / 100) * 45))
  }

  return (
    <svg
      className="viz viz-elements"
      viewBox={`0 0 ${FIELD} ${FIELD}`}
      width="100%"
      role="img"
      aria-label="Поток пяти стихий"
    >
      <defs>
        <marker
          id={`flow-${arrowId}`}
          markerWidth="7"
          markerHeight="7"
          refX="5.6"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0 0.6 L6 3 L0 5.4 z" style={{ fill: 'var(--line2)' }} />
        </marker>
      </defs>

      {ELEMENT_ORDER.map((element, index) => {
        const from = nodePosition(index)
        const to = nodePosition((index + 1) % ELEMENT_ORDER.length)
        const dx = to.x - from.x
        const dy = to.y - from.y
        const length = Math.hypot(dx, dy) || 1
        const gapFrom = radiusOf(element) + 7
        const gapTo = radiusOf(ELEMENT_ORDER[(index + 1) % ELEMENT_ORDER.length] as ElementId) + 11
        return (
          <line
            key={`flow-${element}`}
            x1={from.x + (dx / length) * gapFrom}
            y1={from.y + (dy / length) * gapFrom}
            x2={to.x - (dx / length) * gapTo}
            y2={to.y - (dy / length) * gapTo}
            style={{ stroke: 'var(--line2)' }}
            strokeWidth={1.5}
            markerEnd={`url(#flow-${arrowId})`}
          />
        )
      })}

      {ELEMENT_ORDER.map((element, index) => {
        const point = nodePosition(index)
        const color = elementColor(element)
        const radius = radiusOf(element)
        const isMissing = missing.includes(element)
        const isOverloaded = overloaded.includes(element)
        const isHighlighted = highlight === element
        const percent = shares.get(element)
        return (
          <g key={element}>
            {isHighlighted && (
              <circle
                cx={point.x}
                cy={point.y}
                r={radius + 7}
                fill="none"
                style={{ stroke: 'var(--gold)' }}
                strokeWidth={1.5}
              />
            )}
            {isOverloaded && (
              <circle
                cx={point.x}
                cy={point.y}
                r={radius + 3.5}
                fill="none"
                style={{ stroke: color, opacity: 0.7 }}
                strokeWidth={1.5}
              />
            )}
            <circle
              cx={point.x}
              cy={point.y}
              r={radius}
              style={{ fill: color, fillOpacity: isMissing ? 0.06 : 0.22, stroke: color }}
              strokeWidth={1.5}
              strokeDasharray={isMissing ? '3 3' : undefined}
            />
            <text
              x={point.x}
              y={point.y + radius + 15}
              textAnchor="middle"
              className={`viz-cap${isHighlighted ? ' on' : ''}`}
            >
              {ELEMENT_RU[element]}
              {typeof percent === 'number' ? ` ${percent}%` : ''}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
